import React, { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { every } from 'lodash';
import { Checkbox } from '../../../../../../../../components/checkbox';
import { Dialog } from '../../../../../../../../components/dialog/base';
import { IconCheckbox } from '../../../../../../../../components/icons/icon-checkbox';
import { DayOfWeekInternal, HolidaysHandling } from '../../../../../../../../data-access/gql-types/graphql';
import { DaysOfWeekInternalOption, DaysPeriod } from '../../../../../../../../types/actions';
import { useSelectedDays } from '../../../../hooks/use-selected-days';
import './index.scss';

type SelectedDaysDialogProps = {
  show: boolean;
  setShow: (show: boolean) => void;
  selected: DayOfWeekInternal[];
  holidays: HolidaysHandling;
  onSave: (data: DayOfWeekInternal[], holidaysHandling: HolidaysHandling) => void;
  setShowRepeatPicker: (show: boolean) => void;
};

export const SelectedDaysWithHolidaysDialog: React.FC<SelectedDaysDialogProps> = ({
  show,
  setShow,
  selected,
  holidays,
  onSave,
  setShowRepeatPicker,
}) => {
  const { t } = useTranslation('action');
  const { t: tc } = useTranslation('common');
  const { daysOfWeek } = useSelectedDays();
  const [days, setDays] = useState<DaysOfWeekInternalOption[]>(daysOfWeek);
  const [holidaysHandling, setHolidaysHandling] = useState<boolean>(false);

  useEffect(() => {
    setDays(daysOfWeek.map((x) => ({ ...x, checked: selected.includes(x.value) })));
    setHolidaysHandling(holidays === HolidaysHandling.Perform);
  }, [show]);

  const handleClose = () => {
    setShow(false);
    setShowRepeatPicker(true);
  };

  const handleActionsOnChange = (e: ChangeEvent<HTMLInputElement>, daysPeriod?: DaysPeriod) => {
    let items = Array.from(days);
    const { checked, value } = e.target;

    if (!daysPeriod) {
      const foundIndex = items.findIndex((x) => x.value === value);

      if (foundIndex !== -1) {
        items[foundIndex].checked = checked;
      }
    } else {
      if (daysPeriod === DaysPeriod.Work) {
        items = items.map((x) => {
          if (x.value !== DayOfWeekInternal.Saturday && x.value !== DayOfWeekInternal.Sunday) {
            return {
              ...x,
              checked,
            };
          }

          return { ...x };
        });
      } else {
        items = items.map((x) => {
          if (x.value === DayOfWeekInternal.Saturday || x.value === DayOfWeekInternal.Sunday) {
            return {
              ...x,
              checked,
            };
          }

          return { ...x };
        });

        setHolidaysHandling(checked);
      }
    }

    setDays(items);
  };

  const handleHolidays = (e: ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target;

    setHolidaysHandling(checked);
  };

  const handleSave = () => {
    const selectedDays = days.filter((x) => x.checked).map((x) => x.value);

    onSave(selectedDays, holidaysHandling ? HolidaysHandling.Perform : HolidaysHandling.Ignore);
    setShow(false);
  };

  const workDaysChecked = every(days.slice(0, 5), (x) => x.checked);
  const weekendDaysChecked = every(days.slice(5, 7), (x) => x.checked && holidaysHandling);

  return (
    <Dialog show={show} setShow={setShow}>
      <div className="selected-days-select">
        <div className="dialog-select">
          <h2 className="dialog-select__header">{t('trigger.conditions.time.repeatingOnSelectedDays')}</h2>
          <div>
            <Checkbox checked={workDaysChecked} reverse onChange={(e) => handleActionsOnChange(e, DaysPeriod.Work)}>
              <p>{t('trigger.conditions.time.workDays')}</p>
            </Checkbox>
            <div className="days-period">
              {days.slice(0, 5).map((x) => (
                <Checkbox
                  id={x.value.toString()}
                  key={x.value}
                  checked={x.checked}
                  reverse
                  onChange={handleActionsOnChange}
                >
                  <p>{x.label}</p>
                </Checkbox>
              ))}
            </div>
            <Checkbox
              checked={weekendDaysChecked}
              reverse
              onChange={(e) => handleActionsOnChange(e, DaysPeriod.Weekend)}
            >
              <p>{t('trigger.conditions.time.weekendDays')}</p>
            </Checkbox>
            <div className="days-period">
              {days.slice(5, 7).map((x) => (
                <Checkbox
                  id={x.value.toString()}
                  key={x.value}
                  checked={x.checked}
                  reverse
                  onChange={handleActionsOnChange}
                >
                  <p>{x.label}</p>
                </Checkbox>
              ))}
              <Checkbox id={'holidays'} checked={holidaysHandling} reverse onChange={handleHolidays}>
                <p>{t('trigger.conditions.time.holidays')}</p>
              </Checkbox>
            </div>
          </div>
          <div className="dialog-confirmation__buttons-wrapper">
            <button className="dialog-confirmation__button" onClick={handleClose}>
              {tc('buttons.cancel')}
            </button>
            <button onClick={handleSave} className="dialog-confirmation__button dialog-confirmation__button--primary">
              {tc('buttons.save')}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
